import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { PlacementPayPackagesFilterName } from './filterUtils';
import { IPlacementPayPackagesRequest } from '@AMIEWEB/Order/Store/types';

const extractTreeValues = (nodes: any[]): string[] => {
  if (!Array.isArray(nodes)) return [];
  const out: string[] = [];
  const walk = (node: any) => {
    if (!node) return;

    // Include value for all nodes except the top-level parents
    if (!isNullOrUndefined(node.value)) {
      out.push(String(node.value));
    }

    if (Array.isArray(node.children)) {
      node.children.forEach(walk);
    }
  };

  // Skip first-level parent values, but process their children
  nodes.forEach(node => {
    if (Array.isArray(node.children)) {
      node.children.forEach(walk);
    }
  });

  return Array.from(new Set(out));
};

export const buildActivityStatusPayload = (
  nodes: any[],
): Array<{ activityTypeId: number; activityStatusId: number }> => {
  if (!Array.isArray(nodes)) return [];
  const result: Array<{ activityTypeId: number; activityStatusId: number }> = [];

  nodes.forEach(parent => {
    if (isNullOrUndefined(parent.value)) return;

    const parentValue = parseInt(String(parent.value), 10);
    if (Number.isNaN(parentValue)) return;

    if (Array.isArray(parent.children)) {
      parent.children.forEach((child: any) => {
        if (!isNullOrUndefined(child.value)) {
          const childValue = parseInt(String(child.value), 10);
          if (!Number.isNaN(childValue)) {
            result.push({
              activityTypeId: parentValue,
              activityStatusId: childValue,
            });
          }
        }
      });
    }
  });

  return result;
};

const getSelectedValues = (filters: any, selectedChipFilters: any, key: string) => {
  if (selectedChipFilters && selectedChipFilters[key]) {
    return selectedChipFilters[key];
  }
  return filters?.[key];
};

export const filterPlacementPayPackagesRows = (rows: any[], filterObject: any) => {
  if (!Array.isArray(rows) || !filterObject?.filters) return rows || [];

  const filters = filterObject.filters;
  const selected = filterObject.selectedChipFilters;

  // Archive (single select)
  const archive = getSelectedValues(filters, selected, PlacementPayPackagesFilterName.archiveStatus);
  const archiveValue = archive?.value ?? archive?.[0]?.value;
  const filterArchived = archiveValue === 'Y' ? true : archiveValue === 'N' ? false : null;

  // Placement Status (tree selection)
  const statusNodes = getSelectedValues(filters, selected, PlacementPayPackagesFilterName.placementStatus);
  const selectedStatusIds = extractTreeValues(statusNodes || []);

  // Package Status (multi select)
  const pkgStatuses = getSelectedValues(filters, selected, PlacementPayPackagesFilterName.payPackageStatus) || [];
  const selectedPkgStatuses = Array.isArray(pkgStatuses) ? pkgStatuses.map(x => x?.value ?? x).filter(Boolean) : [];

  // Candidate ID (multi select)
  const candidateIds = getSelectedValues(filters, selected, PlacementPayPackagesFilterName.candidateId) || [];
  const selectedCandidateIds = Array.isArray(candidateIds)
    ? candidateIds.map(x => x?.value ?? x).filter(v => !isNullOrUndefined(v) && `${v}`.length > 0)
    : [];

  // Candidate Name (multi select)
  const candidateNames = getSelectedValues(filters, selected, PlacementPayPackagesFilterName.candidateName) || [];
  const selectedCandidateNames = Array.isArray(candidateNames)
    ? candidateNames
        .map(x => x?.value ?? x)
        .filter(v => typeof v === 'string' && v.trim().length > 0)
        .map(v => v.trim().toLowerCase())
    : [];

  // Order ID (multi select)
  const orderIds = getSelectedValues(filters, selected, PlacementPayPackagesFilterName.orderId) || [];
  const selectedOrderIds = Array.isArray(orderIds)
    ? orderIds
        .map(x => x?.value ?? x)
        .filter(v => !isNullOrUndefined(v) && `${v}`.length > 0)
        .map(String)
    : [];

  return rows.filter(row => {
    // AND across different filters
    if (filterArchived !== null) {
      const archived =
        row?.archived ??
        row?.isArchived ??
        row?.isArchivedPlacement ??
        row?.archive ??
        row?.archiveStatus === 'Y' ??
        false;
      if (Boolean(archived) !== filterArchived) return false;
    }

    if (selectedStatusIds.length > 0) {
      const rowStatusId = !isNullOrUndefined(row?.placementStatusId) ? String(row.placementStatusId) : null;
      if (rowStatusId && !selectedStatusIds.includes(rowStatusId)) return false;
    }

    if (selectedPkgStatuses.length > 0) {
      const status = row?.scenarioStatus ?? row?.payPackageStatus ?? row?.orderPayPackageStatus;
      if (!status || !selectedPkgStatuses.includes(status)) return false;
    }

    if (selectedCandidateIds.length > 0) {
      const rowCandidateId = row?.travelerId ?? row?.candidateId ?? row?.previewDrawerViewerId;
      const rowCandidateIdStr = !isNullOrUndefined(rowCandidateId) ? String(rowCandidateId) : null;
      if (!rowCandidateIdStr || !selectedCandidateIds.map(String).includes(rowCandidateIdStr)) return false;
    }

    if (selectedCandidateNames.length > 0) {
      const rowName = `${row?.candidateName ?? ''}`.trim().toLowerCase();
      if (!rowName || !selectedCandidateNames.includes(rowName)) return false;
    }

    if (selectedOrderIds.length > 0) {
      const rowOrderId = row?.orderId ?? row?.orderID ?? row?.order?.orderId ?? row?.order?.id;
      const rowOrderIdStr = !isNullOrUndefined(rowOrderId) ? String(rowOrderId) : null;
      if (!rowOrderIdStr || !selectedOrderIds.includes(rowOrderIdStr)) return false;
    }

    return true;
  });
};

/** Build API request payload for POST placement/margin/margin-inline-details/placements */
export const buildPlacementPayPackagesPayload = (
  context: { orderId?: number; candidateId?: number; facilityId?: number },
  filters: any,
  selectedChipFilters: any,
  pageNumber: number,
  pageSize: number,
  sortModel?: { field: string; sort: string }[],
): IPlacementPayPackagesRequest => {
  const getSelected = (key: string) => selectedChipFilters?.[key] ?? filters?.[key];

  const archive = getSelected(PlacementPayPackagesFilterName.archiveStatus);
  const archiveValue = archive?.value ?? archive?.[0]?.value;
  const isArchived = archiveValue === 'Y' ? true : archiveValue === 'N' ? false : null;

  const statusNodes = getSelected(PlacementPayPackagesFilterName.placementStatus) || [];
  const placementStatusIds = buildActivityStatusPayload(statusNodes);

  const pkgStatuses = getSelected(PlacementPayPackagesFilterName.payPackageStatus) || [];
  const payPackageStatusIds = Array.isArray(pkgStatuses)
    ? pkgStatuses.map((x: any) => (typeof x === 'object' ? x?.id : null)).filter((id: any) => id != null)
    : [];

  const candidateIdsRaw = getSelected(PlacementPayPackagesFilterName.candidateId) || [];
  const candidateIds = Array.isArray(candidateIdsRaw)
    ? candidateIdsRaw
        .map((x: any) => (typeof x === 'object' ? x?.value ?? x?.id : x))
        .filter((v: any) => v != null && `${v}`.length > 0)
        .map((v: any) => parseInt(String(v), 10))
        .filter((n: number) => !Number.isNaN(n))
    : [];

  const candidateNameOpts = getSelected(PlacementPayPackagesFilterName.candidateName) || [];
  const candidateName =
    Array.isArray(candidateNameOpts) && candidateNameOpts.length > 0
      ? String(candidateNameOpts[0]?.value ?? candidateNameOpts[0]).trim() || null
      : null;

  const orderIdsRaw = getSelected(PlacementPayPackagesFilterName.orderId) || [];
  const orderIds = Array.isArray(orderIdsRaw)
    ? orderIdsRaw
        .map((x: any) => (typeof x === 'object' ? x?.value ?? x?.id : x))
        .filter((v: any) => v != null && `${v}`.length > 0)
        .map((v: any) => parseInt(String(v), 10))
        .filter((n: number) => !Number.isNaN(n))
    : [];

  const sortColumn = sortModel?.[0]?.field != null ? String(sortModel[0].field) : null;
  const sortDirection = sortModel?.[0]?.sort != null ? String(sortModel[0].sort) : null;

  return {
    orderId: context.orderId ?? null,
    candidateId: context.candidateId ?? null,
    facilityId: context.facilityId ?? null,
    orderIds: orderIds.length > 0 ? orderIds : undefined,
    candidateIds: candidateIds.length > 0 ? candidateIds : undefined,
    pageNumber,
    pageSize,
    sortColumn: sortColumn || undefined,
    sortDirection: sortDirection || undefined,
    isArchived: isArchived ?? undefined,
    candidateName: candidateName || undefined,
    placementStatusIds: placementStatusIds.length > 0 ? placementStatusIds : undefined,
    payPackageStatusIds: payPackageStatusIds.length > 0 ? payPackageStatusIds : undefined,
  };
};
